import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./lib/i18n";
import Shell from "./components/Shell";
import { Loader } from "./components/kit";
import Login from "./pages/Login";
import Platform from "./pages/Platform";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import Staff from "./pages/Staff";
import Classes from "./pages/Classes";
import Attendance from "./pages/Attendance";
import Exams from "./pages/Exams";
import Results from "./pages/Results";
import Timetable from "./pages/Timetable";
import Fees from "./pages/Fees";
import Homework from "./pages/Homework";
import Notices from "./pages/Notices";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Updates from "./pages/Updates";
import Receipt from "./pages/Receipt";
import ReportCard from "./pages/ReportCard";
import IdCards from "./pages/IdCards";
import ResetPassword from "./pages/ResetPassword";
import FeeSettings from "./pages/FeeSettings";

const home = (u) => (u.role === "superadmin" ? "/platform" : "/dashboard");

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#05050a]"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={home(user)} replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#05050a]"><Loader /></div>;
  if (user) return <Navigate to={home(user)} replace />;
  return <Login />;
}

const A = ["admin"];
const S = ["admin", "teacher"];
const ALL = ["admin", "teacher", "parent", "student"];
const r = (roles, el) => <Guard roles={roles}>{el}</Guard>;

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  return <Navigate to={user ? home(user) : "/login"} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/forgot-password" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/receipt/:id" element={r(["admin", "parent"], <Receipt />)} />
            <Route path="/report-card/:studentId/:examId" element={r(ALL, <ReportCard />)} />
            <Route path="/id-cards" element={r(["admin", "teacher"], <IdCards />)} />
            <Route element={<Guard><Shell /></Guard>}>
              <Route path="/platform" element={r(["superadmin"], <Platform />)} />
              <Route path="/dashboard" element={r(ALL, <Dashboard />)} />
              <Route path="/students" element={r(S, <Students />)} />
              <Route path="/staff" element={r(A, <Staff />)} />
              <Route path="/classes" element={r(A, <Classes />)} />
              <Route path="/attendance" element={r(["admin", "teacher", "parent"], <Attendance />)} />
              <Route path="/exams" element={r(S, <Exams />)} />
              <Route path="/results" element={r(["parent", "student"], <Results />)} />
              <Route path="/timetable" element={r(ALL, <Timetable />)} />
              <Route path="/fees" element={r(["admin", "parent"], <Fees />)} />
              <Route path="/fee-settings" element={r(["admin"], <FeeSettings />)} />
              <Route path="/homework" element={r(ALL, <Homework />)} />
              <Route path="/notices" element={r(ALL, <Notices />)} />
              <Route path="/reports" element={r(S, <Reports />)} />
              <Route path="/settings" element={r(A, <Settings />)} />
              <Route path="/updates" element={r(["parent", "student"], <Updates />)} />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}
